import { AdminPayments } from "./payments";
import { useState } from "react";
import { api } from "../lib/api";
import type { Lot, Order, Member, SommelierFull, Stats } from "../lib/api";
import { useApi, useMutation } from "../hooks/useApi";
import { useNav, T, Btn, Input, Card, MetricCard, Spinner, ErrorMsg, Table, Modal, StatusBadge, TierBadge } from "../components/shared";

/* ─── Admin shell ──────────────────────────────────────────── */
export function AdminShell({ children, activeTab }: { children: React.ReactNode; activeTab: string }) {
  const { setView } = useNav();
  const tabs = [
    { id: "admin-dash",       label: "Dashboard" },
    { id: "admin-lots",       label: "Lot Manager" },
    { id: "admin-orders",     label: "Orders" },
    { id: "admin-members",    label: "Members" },
    { id: "admin-sommeliers", label: "Sommeliers" },
    { id: "admin-payments",   label: "Payments" },
  ];
  return (
    <div style={{ display: "flex", minHeight: "100vh", paddingTop: 72 }}>
      <div style={{ width: 220, background: T.forestDeep, flexShrink: 0, padding: "40px 0", borderRight: `1px solid rgba(152,121,64,.12)`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "0 24px", marginBottom: 32 }}>
          <p style={{ fontFamily: "'Cinzel',serif", fontSize: ".72rem", letterSpacing: ".28em", textTransform: "uppercase", color: T.ivory, marginBottom: 4 }}>Provenance</p>
          <p style={{ fontSize: ".5rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.brass }}>Admin Console</p>
        </div>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 24px", fontFamily: "'Jost',sans-serif", fontSize: ".66rem", letterSpacing: ".16em", textTransform: "uppercase", background: activeTab === t.id ? `rgba(152,121,64,.15)` : "transparent", color: activeTab === t.id ? T.brassGlow : "rgba(244,239,228,.45)", border: "none", borderLeft: activeTab === t.id ? `2px solid ${T.brass}` : "2px solid transparent", cursor: "pointer", transition: "all .2s" }}>
            {t.label}
          </button>
        ))}
        <div style={{ marginTop: "auto", padding: "24px", borderTop: `1px solid rgba(152,121,64,.1)` }}>
          <button onClick={() => setView("home")} style={{ fontSize: ".54rem", letterSpacing: ".18em", textTransform: "uppercase", color: "rgba(244,239,228,.3)", background: "none", border: "none", cursor: "pointer" }}>← Back to Site</button>
        </div>
      </div>
      <div style={{ flex: 1, background: T.ivory, overflowY: "auto" }}>{children}</div>
    </div>
  );
}

/* ─── Dashboard ─────────────────────────────────────────────── */
export function AdminDash() {
  const { data: stats, loading } = useApi(() => api.stats.get(), []);
  const { data: orders } = useApi(() => api.orders.list(), []);

  if (loading) return <div style={{ padding: 48 }}><Spinner /></div>;
  const s = stats!;
  const recentOrders = (orders ?? []).slice(0, 6);

  return (
    <div style={{ padding: "48px 52px" }} className="fade-in">
      <div style={{ marginBottom: 36 }}>
        <span style={{ fontSize: ".52rem", letterSpacing: ".3em", textTransform: "uppercase", color: T.mist, display: "block", marginBottom: 8 }}>Admin Console</span>
        <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.6rem", fontWeight: 400, letterSpacing: ".08em", color: T.forestDeep }}>Dashboard</h1>
      </div>
      {s && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 2, marginBottom: 40 }}>
            <MetricCard label="Total Revenue" value={`$${(s.revenue.totalCents / 100).toLocaleString()}`} sub={`${s.orders.total} orders`} accent />
            <MetricCard label="Active Members" value={s.members.active} sub={`${s.members.total} total`} />
            <MetricCard label="Active Lots"    value={s.lots.active}   sub="Currently listed" />
            <MetricCard label="Pending"        value={s.orders.pending} sub="Need scheduling" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, marginBottom: 2 }}>
            <Card>
              <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".78rem", letterSpacing: ".14em", color: T.forestDeep, marginBottom: 20 }}>Member Tier Breakdown</h3>
              {(["vault", "reserve", "origin"] as const).map(tier => {
                const count = s.tierBreakdown[tier] ?? 0;
                const pct = s.members.total ? Math.round(count / s.members.total * 100) : 0;
                const colors = { vault: T.forest, reserve: T.brassLt, origin: T.linen };
                return (
                  <div key={tier} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ fontSize: ".58rem", letterSpacing: ".18em", textTransform: "uppercase", color: T.smoke }}>{tier}</span>
                      <span style={{ fontFamily: "'Cormorant',serif", fontSize: ".9rem", color: T.mist }}>{count} · {pct}%</span>
                    </div>
                    <div style={{ height: 3, background: T.ivoryDeep }}>
                      <div style={{ height: "100%", background: colors[tier], width: `${pct}%`, transition: "width 1s ease" }} />
                    </div>
                  </div>
                );
              })}
            </Card>
            <Card>
              <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".78rem", letterSpacing: ".14em", color: T.forestDeep, marginBottom: 20 }}>Order Status</h3>
              {(["scheduled", "roasting", "dispatched", "delivered"] as const).map(status => (
                <div key={status} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.ivoryWarm}` }}>
                  <StatusBadge status={status} />
                  <span style={{ fontFamily: "'Cormorant',serif", fontSize: "1.2rem", color: T.forest }}>{s.statusBreakdown[status] ?? 0}</span>
                </div>
              ))}
            </Card>
          </div>
          <div style={{ marginTop: 2 }}>
            <Card>
              <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".78rem", letterSpacing: ".14em", color: T.forestDeep, marginBottom: 20 }}>Recent Orders</h3>
              <Table
                cols={[
                  { key: "id", label: "Order", render: v => <span style={{ fontFamily: "'Cinzel',serif", fontSize: ".72rem" }}>{v.slice(-8).toUpperCase()}</span> },
                  { key: "memberName", label: "Member" },
                  { key: "lotName",    label: "Lot" },
                  { key: "memberTier", label: "Tier",   render: v => <TierBadge tier={v} /> },
                  { key: "amountDollars", label: "Amount", render: v => `$${v}` },
                  { key: "status",     label: "Status", render: v => <StatusBadge status={v} /> },
                  { key: "scheduledDate", label: "Delivery", render: v => v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—" },
                ]}
                rows={recentOrders}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Lot Manager ────────────────────────────────────────────── */
const BLANK_LOT = { name: "", region: "", country: "", elevation: "", farm: "", varietal: "", process: "", harvest: "", lotSizeKg: "", remaining: "", price: "", tier: "all", status: "active", rarity: "4", notes: "", flavors: "", acidity: "75", sweetness: "75", body: "75", complexity: "75", finish: "75", sortOrder: "0" };

export function AdminLots() {
  const { data: lots, loading, refetch } = useApi(() => api.lots.list(), []);
  const [modal,  setModal]  = useState<"new" | "edit" | null>(null);
  const [filter, setFilter] = useState("all");
  const [form,   setForm]   = useState<Record<string, any>>(BLANK_LOT);

  const { mutate: save, loading: saving, error: saveErr } = useMutation(async () => {
    const payload = {
      ...form,
      lotSizeKg:  parseInt(form.lotSizeKg),
      remaining:  parseInt(form.remaining),
      price:      parseFloat(form.price),
      rarity:     parseInt(form.rarity),
      sortOrder:  parseInt(form.sortOrder ?? 0),
      acidity:    parseInt(form.acidity),
      sweetness:  parseInt(form.sweetness),
      body:       parseInt(form.body),
      complexity: parseInt(form.complexity),
      finish:     parseInt(form.finish),
      flavors: typeof form.flavors === "string" ? form.flavors.split(",").map((s: string) => s.trim()).filter(Boolean) : form.flavors,
    };
    if (modal === "new") await api.lots.create(payload);
    else await api.lots.update(form.id, payload);
    await refetch();
    setModal(null);
  });

  const openNew  = () => { setForm({ ...BLANK_LOT }); setModal("new"); };
  const openEdit = (lot: Lot) => { setForm({ ...lot, price: lot.price, flavors: Array.isArray(lot.flavors) ? lot.flavors.join(", ") : lot.flavors }); setModal("edit"); };
  const archive  = async (id: string) => { await api.lots.archive(id); refetch(); };

  const f = (key: string) => (v: string) => setForm((p: any) => ({ ...p, [key]: v }));

  const filtered = (lots ?? []).filter(l => filter === "all" || l.status === filter);

  return (
    <div style={{ padding: "48px 52px" }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
        <div>
          <span style={{ fontSize: ".52rem", letterSpacing: ".3em", textTransform: "uppercase", color: T.mist, display: "block", marginBottom: 8 }}>Admin</span>
          <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.6rem", fontWeight: 400, letterSpacing: ".08em", color: T.forestDeep }}>Lot Manager</h1>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {["all", "active", "archived"].map(f2 => (
            <button key={f2} onClick={() => setFilter(f2)} style={{ fontFamily: "'Jost',sans-serif", fontSize: ".54rem", letterSpacing: ".18em", textTransform: "uppercase", padding: "8px 16px", background: filter === f2 ? T.forest : "transparent", color: filter === f2 ? T.ivory : T.mist, border: `1px solid ${filter === f2 ? T.forest : T.ivoryDeep}`, cursor: "pointer", transition: "all .2s" }}>{f2}</button>
          ))}
          <Btn onClick={openNew} small>+ Add Lot</Btn>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <Card style={{ padding: 0 }}>
          <Table
            cols={[
              { key: "name",      label: "Lot" },
              { key: "country",   label: "Country" },
              { key: "tier",      label: "Tier",      render: v => <TierBadge tier={v} /> },
              { key: "price",     label: "Price",     render: v => `$${v}/100g` },
              { key: "remaining", label: "Remaining", render: v => <span style={{ color: v < 15 ? T.danger : T.smoke }}>{v}</span> },
              { key: "rarity",    label: "Rarity",    render: v => <span style={{ color: T.brass }}>{"★".repeat(v)}</span> },
              { key: "status",    label: "Status",    render: v => <StatusBadge status={v} /> },
              { key: "id",        label: "",          render: (v, row) => (
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn small variant="ghost" onClick={() => openEdit(row)}>Edit</Btn>
                  {row.status === "active"
                    ? <Btn small variant="ghost" onClick={() => archive(v)} style={{ color: T.danger, borderColor: T.danger }}>Archive</Btn>
                    : <Btn small variant="ghost" onClick={async () => { await api.lots.update(v, { status: "active" }); refetch(); }}>Restore</Btn>
                  }
                </div>
              )},
            ]}
            rows={filtered}
          />
        </Card>
      )}

      {modal && (
        <Modal title={modal === "new" ? "Add New Lot" : `Edit — ${form.name}`} onClose={() => setModal(null)} width={660}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <Input label="Lot Name"    value={form.name}      onChange={f("name")}      required />
            <Input label="Country"     value={form.country}   onChange={f("country")}   required />
            <Input label="Region"      value={form.region}    onChange={f("region")} />
            <Input label="Farm"        value={form.farm}      onChange={f("farm")} />
            <Input label="Elevation"   value={form.elevation} onChange={f("elevation")} />
            <Input label="Varietal"    value={form.varietal}  onChange={f("varietal")} />
            <Input label="Process"     value={form.process}   onChange={f("process")} />
            <Input label="Harvest"     value={form.harvest}   onChange={f("harvest")} />
            <Input label="Lot Size (kg)" type="number" value={form.lotSizeKg} onChange={f("lotSizeKg")} />
            <Input label="Remaining"   type="number"  value={form.remaining}  onChange={f("remaining")} />
            <Input label="Price per 100g ($)" type="number" value={form.price} onChange={f("price")} />
            <Input label="Rarity (1–5)"   type="number" value={form.rarity}   onChange={f("rarity")} />
            <Input label="Tier" type="select" value={form.tier} onChange={f("tier")}
              options={[{ value: "all", label: "All Tiers" }, { value: "reserve", label: "Reserve & Vault" }, { value: "vault", label: "Vault Only" }]} />
            <Input label="Status" type="select" value={form.status} onChange={f("status")}
              options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }]} />
          </div>
          <Input label="Tasting Notes" type="textarea" value={form.notes} onChange={f("notes")} rows={2} />
          <Input label="Flavour Tags (comma-separated)" value={typeof form.flavors === "string" ? form.flavors : (form.flavors ?? []).join(", ")} onChange={f("flavors")} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }}>
            {["acidity", "sweetness", "body", "complexity", "finish"].map(attr => (
              <div key={attr}>
                <label style={{ display: "block", fontSize: ".5rem", letterSpacing: ".18em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>{attr} <strong style={{ color: T.brass }}>{form[attr]}</strong></label>
                <input type="range" min={0} max={100} value={form[attr] ?? 75} onChange={e => setForm((p: any) => ({ ...p, [attr]: e.target.value }))} style={{ width: "100%", accentColor: T.forest }} />
              </div>
            ))}
          </div>
          {saveErr && <ErrorMsg msg={saveErr} />}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setModal(null)} small>Cancel</Btn>
            <Btn onClick={() => save()} disabled={saving} small>{saving ? "Saving…" : "Save Lot"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Orders ────────────────────────────────────────────────── */
export function AdminOrders() {
  const { data: orders, loading, refetch } = useApi(() => api.orders.list(), []);
  const { data: sommeliers } = useApi(() => api.sommeliers.list(), []);
  const [filter,   setFilter]   = useState("all");
  const [selected, setSelected] = useState<Order | null>(null);

  const updateOrder = async (id: string, patch: Partial<Order>) => {
    await api.orders.update(id, patch);
    await refetch();
    setSelected(null);
  };

  const filtered = (orders ?? []).filter(o => filter === "all" || o.status === filter);

  return (
    <div style={{ padding: "48px 52px" }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
        <div>
          <span style={{ fontSize: ".52rem", letterSpacing: ".3em", textTransform: "uppercase", color: T.mist, display: "block", marginBottom: 8 }}>Admin</span>
          <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.6rem", fontWeight: 400, letterSpacing: ".08em", color: T.forestDeep }}>Orders</h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["all", "pending", "scheduled", "roasting", "dispatched", "delivered"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ fontFamily: "'Jost',sans-serif", fontSize: ".52rem", letterSpacing: ".16em", textTransform: "uppercase", padding: "7px 14px", background: filter === f ? T.forest : "transparent", color: filter === f ? T.ivory : T.mist, border: `1px solid ${filter === f ? T.forest : T.ivoryDeep}`, cursor: "pointer", transition: "all .2s" }}>{f}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <Card style={{ padding: 0 }}>
          <Table
            cols={[
              { key: "id",            label: "Order",    render: v => <span style={{ fontFamily: "'Cinzel',serif", fontSize: ".72rem" }}>{v.slice(-8).toUpperCase()}</span> },
              { key: "memberName",    label: "Member" },
              { key: "lotName",       label: "Lot" },
              { key: "memberTier",    label: "Tier",     render: v => <TierBadge tier={v} /> },
              { key: "amountDollars", label: "Amount",   render: v => `$${v}` },
              { key: "status",        label: "Status",   render: v => <StatusBadge status={v} /> },
              { key: "scheduledDate", label: "Delivery", render: v => v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—" },
              { key: "sommelierName", label: "Sommelier", render: v => <span style={{ fontSize: ".72rem", color: v ? T.smoke : T.mist }}>{v ?? "Unassigned"}</span> },
              { key: "id", label: "", render: (_, row) => <Btn small variant="ghost" onClick={() => setSelected(row)}>Manage</Btn> },
            ]}
            rows={filtered}
            onRowClick={setSelected}
          />
        </Card>
      )}

      {selected && (
        <Modal title={`Order ${selected.id.slice(-8).toUpperCase()}`} onClose={() => setSelected(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
            {[["Member", selected.memberName], ["Lot", selected.lotName], ["Tier", selected.memberTier], ["Amount", `$${selected.amountDollars}`], ["Created", new Date(selected.createdAt).toLocaleDateString("en-GB")], ["Delivery", selected.scheduledDate ?? "—"]].map(([k, v]) => (
              <div key={k} style={{ padding: "12px", background: T.ivoryWarm }}>
                <p style={{ fontSize: ".5rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>{k}</p>
                <p style={{ fontSize: ".82rem", color: T.ink }}>{k === "Tier" ? <TierBadge tier={v ?? undefined} /> : v}</p>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 10 }}>Update Status</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["pending", "scheduled", "roasting", "dispatched", "delivered"].map(s => (
                <button key={s} onClick={() => updateOrder(selected.id, { status: s as any })}
                  style={{ fontFamily: "'Jost',sans-serif", fontSize: ".54rem", letterSpacing: ".16em", textTransform: "uppercase", padding: "8px 16px", background: selected.status === s ? T.forest : "transparent", color: selected.status === s ? T.ivory : T.smoke, border: `1px solid ${selected.status === s ? T.forest : T.ivoryDeep}`, cursor: "pointer", transition: "all .2s" }}>{s}</button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 10 }}>Assign Sommelier</p>
            <select value={selected.sommelierId ?? ""} onChange={e => updateOrder(selected.id, { sommelierId: e.target.value || undefined } as any)}
              style={{ width: "100%", padding: "10px 14px", border: `1px solid ${T.ivoryDeep}`, background: T.ivory, fontSize: ".8rem", color: T.ink }}>
              <option value="">— Unassigned —</option>
              {(sommeliers ?? []).map(s => <option key={s.id} value={s.id}>{s.name} ({s.city})</option>)}
            </select>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn small variant="ghost" onClick={() => setSelected(null)}>Close</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Members ─────────────────────────────────────────────────── */
export function AdminMembers() {
  const { data: orders } = useApi(() => api.orders.list(), []);
  const [search,     setSearch]     = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [selected,   setSelected]   = useState<Member | null>(null);

  const { data: members, loading, refetch } = useApi(
    () => api.members.list({ search: search || undefined, tier: filterTier !== "all" ? filterTier : undefined }),
    [search, filterTier]
  );

  const updateMember = async (id: string, patch: Partial<Member>) => {
    await api.members.update(id, patch);
    await refetch();
    setSelected(null);
  };

  const orderCount = (id: string) => (orders ?? []).filter(o => o.memberId === id).length;

  return (
    <div style={{ padding: "48px 52px" }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
        <div>
          <span style={{ fontSize: ".52rem", letterSpacing: ".3em", textTransform: "uppercase", color: T.mist, display: "block", marginBottom: 8 }}>Admin</span>
          <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.6rem", fontWeight: 400, letterSpacing: ".08em", color: T.forestDeep }}>Members</h1>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
            style={{ padding: "9px 16px", border: `1px solid ${T.ivoryDeep}`, background: T.white, fontSize: ".8rem", width: 220 }} />
          {["all", "vault", "reserve", "origin"].map(f => (
            <button key={f} onClick={() => setFilterTier(f)} style={{ fontFamily: "'Jost',sans-serif", fontSize: ".52rem", letterSpacing: ".18em", textTransform: "uppercase", padding: "9px 14px", background: filterTier === f ? T.forest : "transparent", color: filterTier === f ? T.ivory : T.mist, border: `1px solid ${filterTier === f ? T.forest : T.ivoryDeep}`, cursor: "pointer", transition: "all .2s" }}>{f}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <Card style={{ padding: 0 }}>
          <Table
            cols={[
              { key: "name",      label: "Name" },
              { key: "email",     label: "Email",   muted: true },
              { key: "tier",      label: "Tier",    render: v => <TierBadge tier={v} /> },
              { key: "city",      label: "City" },
              { key: "createdAt", label: "Joined",  render: v => new Date(v).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) },
              { key: "status",    label: "Status",  render: v => <StatusBadge status={v} /> },
              { key: "id",        label: "Orders",  render: v => <span style={{ fontFamily: "'Cormorant',serif", fontSize: "1rem", color: T.mist }}>{orderCount(v)}</span> },
              { key: "id",        label: "",        render: (_, row) => <Btn small variant="ghost" onClick={() => setSelected(row)}>View</Btn> },
            ]}
            rows={members ?? []}
            onRowClick={setSelected}
          />
        </Card>
      )}

      {selected && (
        <MemberModal member={selected} onClose={() => setSelected(null)} onUpdate={updateMember} />
      )}
    </div>
  );
}

function MemberModal({ member, onClose, onUpdate }: { member: Member; onClose: () => void; onUpdate: (id: string, patch: any) => void }) {
  const [note, setNote] = useState(member.sommelierNote ?? "");
  return (
    <Modal title={member.name} onClose={onClose} width={580}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
        {[["Email", member.email], ["Tier", member.tier], ["City", member.city ?? "—"], ["Joined", new Date(member.createdAt).toLocaleDateString("en-GB")], ["Status", member.status]].map(([k, v]) => (
          <div key={k} style={{ padding: "12px", background: T.ivoryWarm }}>
            <p style={{ fontSize: ".5rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>{k}</p>
            <p style={{ fontSize: ".82rem", color: T.ink }}>{k === "Tier" ? <TierBadge tier={v ?? undefined} /> : k === "Status" ? <StatusBadge status={v ?? ""} /> : v}</p>
          </div>
        ))}
      </div>
      {member.palateAcidity !== undefined && (
        <div style={{ marginBottom: 20, padding: "16px", background: T.ivoryWarm }}>
          <p style={{ fontSize: ".52rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 12 }}>Palate Profile</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {([["Acidity", member.palateAcidity], ["Sweetness", member.palateSweetness], ["Body", member.palateBody], ["Complexity", member.palateComplexity]] as [string, number | undefined][]).map(([k, v]) => (
              <div key={k}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: ".56rem", letterSpacing: ".12em", textTransform: "uppercase", color: T.smoke }}>{k}</span>
                  <span style={{ fontFamily: "'Cormorant',serif", fontSize: ".85rem", color: T.brass }}>{v ?? 5}/10</span>
                </div>
                <div style={{ height: 2, background: T.ivoryDeep }}>
                  <div style={{ height: "100%", background: T.forest, width: `${(v ?? 5) * 10}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {member.sommelier && (
        <div style={{ marginBottom: 20, padding: "14px", background: T.ivoryWarm, borderLeft: `2px solid ${T.brass}` }}>
          <p style={{ fontSize: ".5rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>Assigned Sommelier</p>
          <p style={{ fontSize: ".82rem", color: T.forestDeep }}>{member.sommelier.name} · {member.sommelier.city}</p>
        </div>
      )}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: ".52rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 8 }}>Sommelier Notes</p>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
          style={{ width: "100%", padding: "10px 14px", border: `1px solid ${T.ivoryDeep}`, background: T.ivory, fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".82rem", color: T.smoke, resize: "vertical", lineHeight: 1.7 }} />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
        <Btn small variant={member.status === "active" ? "ghost" : "primary"} onClick={() => onUpdate(member.id, { status: member.status === "active" ? "paused" : "active" })}>
          {member.status === "active" ? "Pause Membership" : "Reactivate"}
        </Btn>
        <Btn small onClick={() => onUpdate(member.id, { sommelierNote: note })}>Save & Close</Btn>
      </div>
    </Modal>
  );
}

/* ─── Sommeliers ────────────────────────────────────────────── */
export function AdminSommeliers() {
  const { data: members } = useApi(() => api.members.list(), []);
  const { data: orders,    refetch: refetchOrders } = useApi(() => api.orders.list(), []);
  const { data: sommeliers, loading, refetch } = useApi(() => api.sommeliers.list(), []);
  const [modal,    setModal]    = useState<"new" | "edit" | null>(null);
  const [form,     setForm]     = useState<{ id?: string; name: string; city: string; email: string; phone: string; status: string; memberIds: string[] }>({ name: "", city: "", email: "", phone: "", status: "active", memberIds: [] });

  const { mutate: save, loading: saving, error: saveErr } = useMutation(async () => {
    const { memberIds, ...rest } = form;
    if (modal === "new") await api.sommeliers.create(rest);
    else await api.sommeliers.update(form.id!, { ...rest, memberIds });
    await refetch();
    setModal(null);
  });

  const openNew  = () => { setForm({ name: "", city: "", email: "", phone: "", status: "active", memberIds: [] }); setModal("new"); };
  const openEdit = (s: SommelierFull) => { setForm({ id: s.id, name: s.name, city: s.city, email: s.email ?? "", phone: s.phone ?? "", status: s.status, memberIds: s.members.map(m => m.id!) }); setModal("edit"); };

  const unassignedOrders = (orders ?? []).filter(o => !o.sommelierId && o.status === "scheduled");

  const assignOrder = async (orderId: string, sommelierId: string) => {
    await api.orders.update(orderId, { sommelierId } as any);
    refetchOrders();
  };

  return (
    <div style={{ padding: "48px 52px" }} className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
        <div>
          <span style={{ fontSize: ".52rem", letterSpacing: ".3em", textTransform: "uppercase", color: T.mist, display: "block", marginBottom: 8 }}>Admin</span>
          <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: "1.6rem", fontWeight: 400, letterSpacing: ".08em", color: T.forestDeep }}>Sommeliers</h1>
        </div>
        <Btn small onClick={openNew}>+ Add Sommelier</Btn>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2, marginBottom: 32 }}>
          {(sommeliers ?? []).map(s => (
            <Card key={s.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <p style={{ fontFamily: "'Cinzel',serif", fontSize: ".88rem", letterSpacing: ".1em", color: T.forestDeep, marginBottom: 4 }}>{s.name}</p>
                  <p style={{ fontSize: ".6rem", letterSpacing: ".18em", textTransform: "uppercase", color: T.mist }}>{s.city}</p>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                {[["Members", s.members.length], ["Upcoming", s.upcomingCount]].map(([l, v]) => (
                  <div key={l as string} style={{ flex: 1, textAlign: "center", padding: "12px 0", background: T.ivoryWarm }}>
                    <div style={{ fontFamily: "'Cormorant',serif", fontSize: "1.6rem", color: T.forest }}>{v}</div>
                    <div style={{ fontSize: ".48rem", letterSpacing: ".18em", textTransform: "uppercase", color: T.mist }}>{l}</div>
                  </div>
                ))}
              </div>
              {s.members.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {s.members.map(m => (
                    <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${T.ivoryWarm}` }}>
                      <span style={{ fontSize: ".75rem", color: T.smoke }}>{m.name}</span>
                      <TierBadge tier={m.tier} />
                    </div>
                  ))}
                </div>
              )}
              <Btn small variant="ghost" onClick={() => openEdit(s)}>Edit</Btn>
            </Card>
          ))}
        </div>
      )}

      {unassignedOrders.length > 0 && (
        <Card>
          <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".78rem", letterSpacing: ".14em", color: T.forestDeep, marginBottom: 20 }}>Unassigned Scheduled Deliveries</h3>
          <Table
            cols={[
              { key: "id",            label: "Order",    render: v => <span style={{ fontFamily: "'Cinzel',serif", fontSize: ".72rem" }}>{v.slice(-8).toUpperCase()}</span> },
              { key: "memberName",    label: "Member" },
              { key: "lotName",       label: "Lot" },
              { key: "memberTier",    label: "Tier",     render: v => <TierBadge tier={v} /> },
              { key: "scheduledDate", label: "Date",     render: v => v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—" },
              { key: "id",            label: "Sommelier", render: (v, row) => (
                <select defaultValue="" onChange={e => assignOrder(v, e.target.value)}
                  style={{ padding: "6px 10px", border: `1px solid ${T.ivoryDeep}`, background: T.ivory, fontSize: ".72rem", color: T.ink }}>
                  <option value="" disabled>Assign…</option>
                  {(sommeliers ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )},
            ]}
            rows={unassignedOrders}
          />
        </Card>
      )}

      {modal && (
        <Modal title={modal === "new" ? "Add Sommelier" : `Edit — ${form.name}`} onClose={() => setModal(null)}>
          <Input label="Full Name" value={form.name}   onChange={v => setForm(p => ({ ...p, name: v }))}  required />
          <Input label="City"      value={form.city}   onChange={v => setForm(p => ({ ...p, city: v }))}  required />
          <Input label="Email"     value={form.email}  onChange={v => setForm(p => ({ ...p, email: v }))} type="email" />
          <Input label="Phone"     value={form.phone}  onChange={v => setForm(p => ({ ...p, phone: v }))} />
          <Input label="Status" type="select" value={form.status} onChange={v => setForm(p => ({ ...p, status: v }))}
            options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
          {modal === "edit" && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 12 }}>Assign Members</p>
              {(members ?? []).map(m => (
                <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.ivoryWarm}`, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.memberIds.includes(m.id)}
                    onChange={e => setForm(p => ({ ...p, memberIds: e.target.checked ? [...p.memberIds, m.id] : p.memberIds.filter(x => x !== m.id) }))}
                    style={{ accentColor: T.forest }} />
                  <span style={{ flex: 1, fontSize: ".78rem", color: T.smoke }}>{m.name}</span>
                  <TierBadge tier={m.tier} />
                  <span style={{ fontSize: ".62rem", color: T.mist }}>{m.city}</span>
                </label>
              ))}
            </div>
          )}
          {saveErr && <ErrorMsg msg={saveErr} />}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Btn variant="ghost" small onClick={() => setModal(null)}>Cancel</Btn>
            <Btn small onClick={() => save()} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
